import React, { useState, useEffect } from 'react';
import { servicePlans } from '../../data/servicePlans';
import { bookingConfig } from '../../config/bookingConfig';
import { Address } from '../../utils/addressUtils';
import { platformSitePricingService } from '../../services/platformSitePricingService';
import { subServicesData, SubService } from '../../data/servicesData';
import './GroupBooking.css';

interface ServiceBookingConfig {
  cartItem: any;
  selectedPlan: any | null;
  selectedDate: string;
  selectedTime: string;
  selectedAddress: Address | null;
  isConfigured: boolean;
  availablePlans?: any[]; // Dynamic plans with backend pricing
}

interface ServiceAccordionItemProps {
  serviceConfig: ServiceBookingConfig;
  index: number;
  isActive: boolean;
  userAddresses: Address[];
  useGlobalAddress: boolean;
  globalSelectedAddress: Address | null;
  updateServiceConfig: (index: number, updates: Partial<ServiceBookingConfig>) => void;
  onToggle: () => void;
  availablePlans?: any[]; // Dynamic plans with backend pricing
  performAtAntaryatra?: boolean;
  isPlatformPricingConfirmed?: boolean;
  onPlatformPricing?: (serviceId: number, selectedPlan: any) => Promise<void>;
  onShowPlatformModal?: (serviceId: number, selectedPlan: any) => void;
}

const ServiceAccordionItem: React.FC<ServiceAccordionItemProps> = ({
  serviceConfig,
  index,
  isActive,
  userAddresses,
  useGlobalAddress,
  globalSelectedAddress,
  updateServiceConfig,
  onToggle,
  availablePlans = [], // Default to empty array if not provided
  performAtAntaryatra = false,
  isPlatformPricingConfirmed = false,
  onPlatformPricing,
  onShowPlatformModal
}) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [platformSitePricing, setPlatformSitePricing] = useState<any>(null);

  // Helper function to find original service data from cart item
  const findOriginalService = (cartItemName: string): SubService | null => {
    for (const [category, services] of Object.entries(subServicesData)) {
      const service = services.find(s => s.name === cartItemName);
      if (service) return service;
    }
    return null;
  };

  const originalService = findOriginalService(serviceConfig.cartItem.name);

  // Fetch platform pricing for this specific service
  useEffect(() => {
    if (performAtAntaryatra && serviceConfig.cartItem) {
      const fetchPlatformPricing = async () => {
        try {
          const serviceId = parseInt(serviceConfig.cartItem.id.replace('service-', '')) || 1;
          console.log(`🚀 Fetching platform pricing for service ${serviceId}:`, serviceConfig.cartItem.name);
          
          const platformPricing = await platformSitePricingService.getPlatformSitePricing(serviceId);
          console.log(`✅ Platform pricing fetched for service ${serviceId}:`, platformPricing);
          setPlatformSitePricing(platformPricing);
        } catch (error) {
          console.error(`❌ Error fetching platform pricing for service ${serviceConfig.cartItem.name}:`, error);
        }
      };

      fetchPlatformPricing();
    } else {
      setPlatformSitePricing(null);
    }
  }, [performAtAntaryatra, serviceConfig.cartItem]);

  // Auto-update selectedPlan with platform pricing when platform site is active
  useEffect(() => {
    if (performAtAntaryatra && platformSitePricing && serviceConfig.selectedPlan && !serviceConfig.selectedPlan.isPlatformPricing) {
      console.log('🔄 Auto-updating selectedPlan with platform pricing for:', serviceConfig.cartItem.name);
      
      // Find matching platform plan for current selected plan
      const matchingPlatformPlan = platformSitePricing.plans.find((platformPlan: any) => {
        const planCode = serviceConfig.selectedPlan.name.toLowerCase().replace(/[^a-z]/g, '');
        const platformPlanCode = platformPlan.planCode.toLowerCase();
        return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
      });
      
      if (matchingPlatformPlan) {
        // Create modified plan with platform pricing
        const platformUpdatedPlan = {
          ...serviceConfig.selectedPlan,
          price: `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}`,
          dbPlan: {
            basePrice: matchingPlatformPlan.basePrice,
            gstAmount: matchingPlatformPlan.gstAmount || 0,
            totalAmount: matchingPlatformPlan.totalAmount || matchingPlatformPlan.basePrice
          },
          isPlatformPricing: true // Flag to identify this as platform pricing
        };
        
        console.log('🏹 Auto-updated plan with platform pricing:', {
          service: serviceConfig.cartItem.name,
          originalPrice: serviceConfig.selectedPlan.price,
          platformPrice: platformUpdatedPlan.price,
          platformTotalAmount: matchingPlatformPlan.totalAmount
        });
        
        // Update the service config with platform pricing
        updateServiceConfig(index, { selectedPlan: platformUpdatedPlan });
      }
    }
  }, [performAtAntaryatra, platformSitePricing, serviceConfig.selectedPlan, serviceConfig.cartItem.name, updateServiceConfig, index]);

  // Reset to original pricing when platform site is deselected
  useEffect(() => {
    if (!performAtAntaryatra && serviceConfig.selectedPlan?.isPlatformPricing) {
      console.log('🔄 Reverting to original pricing for:', serviceConfig.cartItem.name);
      
      // Find the original plan from available plans
      const originalPlan = plans.find(plan => plan.id === serviceConfig.selectedPlan.id);
      if (originalPlan) {
        const { isPlatformPricing, dbPlan, ...cleanPlan } = originalPlan; // Remove platform pricing flags
        updateServiceConfig(index, { selectedPlan: cleanPlan });
      }
    }
  }, [performAtAntaryatra, serviceConfig.selectedPlan, serviceConfig.cartItem.name, plans, updateServiceConfig, index]);

  useEffect(() => {
    // Use availablePlans if provided (with backend pricing), otherwise fallback to static plans
    if (availablePlans && availablePlans.length > 0) {
      console.log('🚀 ServiceAccordionItem using dynamic plans for', serviceConfig.cartItem.name, ':', availablePlans.map(p => ({ name: p.name, price: p.price })));
      setPlans(availablePlans);
    } else {
      // Fallback to static plans
      console.log('⚠️ ServiceAccordionItem falling back to static plans for', serviceConfig.cartItem.name);
      const serviceKey = serviceConfig.cartItem.name.toLowerCase().replace(/\s+/g, '-');
      const staticPlans = servicePlans[serviceKey] || [];
      setPlans(staticPlans);
    }
  }, [availablePlans, serviceConfig.cartItem.name]);
  
  // Auto-select default plan if none selected and we have plans
  useEffect(() => {
    if (plans.length > 0 && !serviceConfig.selectedPlan) {
      const defaultPlan = plans.find(plan => plan.popular) || plans[0];
      console.log('🎯 Auto-selecting default plan for', serviceConfig.cartItem.name, ':', defaultPlan?.name, 'at', defaultPlan?.price);
      updateServiceConfig(index, { selectedPlan: defaultPlan });
    }
  }, [plans, serviceConfig.selectedPlan, index, updateServiceConfig, serviceConfig.cartItem.name]);

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 18; hour++) {
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      const time12 = hour <= 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
      if (hour === 12) {
        slots.push({ value: time24, label: '12:00 PM' });
      } else {
        slots.push({ value: time24, label: time12 });
      }
    }
    return slots;
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + bookingConfig.minDaysFromToday);
    return today.toISOString().split('T')[0];
  };

  return (
    <div 
      className={`service-accordion-item ${isActive ? 'active' : ''}`}
      data-accordion-index={index}
    >
      <div className="service-accordion-header" onClick={onToggle}>
        <div className="service-info">
          <img 
            src={serviceConfig.cartItem.image || '/images/puja_service.jpg'} 
            alt={serviceConfig.cartItem.name} 
            className="service-thumb" 
          />
          <div className="service-details">
            <h3>{serviceConfig.cartItem.name}</h3>
            <p className="service-price">
              {(() => {
                if (!serviceConfig.selectedPlan) return serviceConfig.cartItem.price;
                
                // If platform site is selected and platform pricing is available, use platform pricing
                if (performAtAntaryatra && platformSitePricing) {
                  const matchingPlatformPlan = platformSitePricing.plans.find((platformPlan: any) => {
                    const planCode = serviceConfig.selectedPlan.name.toLowerCase().replace(/[^a-z]/g, '');
                    const platformPlanCode = platformPlan.planCode.toLowerCase();
                    return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
                  });
                  return matchingPlatformPlan ? `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}` : serviceConfig.selectedPlan.price;
                }
                
                return serviceConfig.selectedPlan.price;
              })()}
            </p>
            <p className="service-duration">{serviceConfig.cartItem.duration || 'Standard duration'}</p>
          </div>
        </div>
        <div className="service-status">
          {serviceConfig.isConfigured ? (
            <span className="status-badge configured">✅ Configured</span>
          ) : (
            <span className="status-badge pending">⏳ Pending</span>
          )}
          <svg className={`chevron ${isActive ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z"/>
          </svg>
        </div>
      </div>
      
      {isActive && (
        <div className="service-accordion-content">
          {/* Plan Selection */}
          <div className="config-section">
            <h4>Select Plan</h4>
            <div className="plans-grid">
              {plans.map((plan) => {
                // Get display price based on Antaryatra selection
                const getDisplayPrice = (plan: any) => {
                  if (performAtAntaryatra && platformSitePricing) {
                    // Find matching platform plan for this plan
                    const matchingPlatformPlan = platformSitePricing.plans.find((platformPlan: any) => {
                      const planCode = plan.name.toLowerCase().replace(/[^a-z]/g, '');
                      const platformPlanCode = platformPlan.planCode.toLowerCase();
                      return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
                    });
                    return matchingPlatformPlan ? `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}` : plan.price;
                  }
                  return plan.price;
                };

                return (
                  <div
                    key={`${plan.id}-${performAtAntaryatra ? 'platform' : 'user'}`}
                    className={`plan-card ${serviceConfig.selectedPlan?.id === plan.id ? 'selected' : ''}`}
                    onClick={async () => {
                      // Create the plan object to update with
                      let planToUpdate = plan;
                      
                      // If Antaryatra is selected and platform pricing is available, 
                      // update the plan with platform pricing data
                      if (performAtAntaryatra && platformSitePricing) {
                        const matchingPlatformPlan = platformSitePricing.plans.find((platformPlan: any) => {
                          const planCode = plan.name.toLowerCase().replace(/[^a-z]/g, '');
                          const platformPlanCode = platformPlan.planCode.toLowerCase();
                          return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
                        });
                        
                        if (matchingPlatformPlan) {
                          // Create modified plan with platform pricing
                          planToUpdate = {
                            ...plan,
                            price: `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}`,
                            dbPlan: {
                              basePrice: matchingPlatformPlan.basePrice,
                              gstAmount: matchingPlatformPlan.gstAmount || 0,
                              totalAmount: matchingPlatformPlan.totalAmount || matchingPlatformPlan.basePrice
                            },
                            isPlatformPricing: true // Flag to identify this as platform pricing
                          };
                          console.log('🏹 Updated plan with platform pricing:', {
                            originalPrice: plan.price,
                            platformPrice: planToUpdate.price,
                            platformTotalAmount: matchingPlatformPlan.totalAmount
                          });
                        }
                      }
                      
                      updateServiceConfig(index, { selectedPlan: planToUpdate });
                      
                      // If Antaryatra is selected, handle platform pricing API calls
                      if (performAtAntaryatra) {
                        const serviceId = parseInt(serviceConfig.cartItem.id.replace('service-', '')) || 1;
                        
                        if (isPlatformPricingConfirmed && onPlatformPricing) {
                          // Update platform pricing silently for the new plan
                          await onPlatformPricing(serviceId, plan);
                        } else if (onShowPlatformModal) {
                          // Show modal for first-time platform pricing
                          onShowPlatformModal(serviceId, plan);
                        }
                      }
                    }}
                  >
                    <div className="plan-header">
                      <h5>{plan.name}</h5>
                      {plan.popular && <span className="popular-badge">Popular</span>}
                      {performAtAntaryatra && platformSitePricing && (
                        <span className="platform-price-tag">📍 Antaryatra Site, Kashi</span>
                      )}
                    </div>
                    <div className="plan-price">{getDisplayPrice(plan)}</div>
                    <div className="plan-duration">{plan.duration}</div>
                    <ul className="plan-features">
                      {plan.includes?.slice(0, 3).map((feature: string, idx: number) => (
                        <li key={idx}>✓ {feature}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date Selection */}
          <div className="config-section">
            <h4>Select Date</h4>
            <input
              type="date"
              min={getMinDate()}
              value={serviceConfig.selectedDate}
              onChange={(e) => updateServiceConfig(index, { selectedDate: e.target.value })}
              className="date-input"
            />
          </div>

          {/* Time Selection */}
          <div className="config-section">
            <h4>Preferred Time (Indicative)</h4>
            <div className="time-slots">
              {generateTimeSlots().map((slot) => (
                <button
                  key={slot.value}
                  className={`time-slot ${serviceConfig.selectedTime === slot.value ? 'selected' : ''}`}
                  onClick={() => updateServiceConfig(index, { selectedTime: slot.value })}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>

          {/* Individual Address Selection (if not using global address) */}
          {!useGlobalAddress && (
            <div className="config-section">
              <h4>Select Address</h4>
              {userAddresses.length > 0 ? (
                <div className="address-options">
                  {userAddresses.map((address) => (
                    <div
                      key={address.id}
                      className={`address-option ${serviceConfig.selectedAddress?.id === address.id ? 'selected' : ''}`}
                      onClick={() => updateServiceConfig(index, { selectedAddress: address })}
                    >
                      <div className="address-info">
                        <h5>{address.name}</h5>
                        <p>{address.line1}</p>
                        {address.line2 && <p>{address.line2}</p>}
                        <p>{address.cityName || address.city}, {address.stateName || address.state} - {address.postalCode}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No addresses found. Please add an address first.</p>
              )}
            </div>
          )}

          {/* Configuration Summary */}
          <div className="config-summary">
            <h4>Configuration Summary</h4>
            <div className="summary-item">
              <span>Plan:</span>
              <span>{serviceConfig.selectedPlan?.name || 'Not selected'}</span>
            </div>
            <div className="summary-item">
              <span>Date:</span>
              <span>{serviceConfig.selectedDate || 'Not selected'}</span>
            </div>
            <div className="summary-item">
              <span>Time:</span>
              <span>{serviceConfig.selectedTime || 'Not selected'}</span>
            </div>
            <div className="summary-item">
              <span>Address:</span>
              <span>
                {useGlobalAddress 
                  ? (globalSelectedAddress ? globalSelectedAddress.name : 'Global address not selected')
                  : (serviceConfig.selectedAddress ? serviceConfig.selectedAddress.name : 'Not selected')
                }
              </span>
            </div>
            <div className="summary-item">
              <span>Status:</span>
              <span className={serviceConfig.isConfigured ? 'configured' : 'pending'}>
                {serviceConfig.isConfigured ? 'Ready for booking' : 'Incomplete configuration'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceAccordionItem;